import { readdir, readFile, writeFile, mkdir, unlink, stat } from "node:fs/promises";
import { join, basename, extname } from "node:path";
import { parseFrontmatter, serializeFrontmatter } from "@vortex-os/core";
import type { Memory, MemoryFrontmatter } from "./types.js";

/**
 * A directory-backed memory store.
 *
 * Each memory lives in a single `.md` file. The `MEMORY.md` (generated index)
 * and `_INDEX.md` (Obsidian-friendly index) files are excluded — they are
 * derived views, not memories themselves.
 */
export class MemoryStore {
  constructor(readonly dir: string) {}

  /** Ensure the backing directory exists. Safe to call repeatedly. */
  async ensure(): Promise<void> {
    await mkdir(this.dir, { recursive: true });
  }

  /** List memory ids (filename stems), sorted lexicographically. */
  async list(): Promise<readonly string[]> {
    try {
      const entries = await readdir(this.dir);
      return entries
        .filter((e) => e.endsWith(".md") && e !== "MEMORY.md" && e !== "_INDEX.md")
        .map((e) => basename(e, extname(e)))
        .sort();
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw e;
    }
  }

  /** Read a memory by id. Throws if the file does not exist. */
  async read(id: string): Promise<Memory> {
    const raw = await readFile(this.pathFor(id), "utf8");
    const { frontmatter, body } = parseFrontmatter<MemoryFrontmatter>(raw);
    return { id, frontmatter, body };
  }

  /** Write (or overwrite) a memory. Ensures the directory exists first. */
  async write(memory: Memory): Promise<void> {
    await this.ensure();
    const source = serializeFrontmatter({
      frontmatter: memory.frontmatter,
      body: memory.body,
    });
    await writeFile(this.pathFor(memory.id), source, "utf8");
  }

  /** Delete a memory. Returns false if it did not exist. */
  async delete(id: string): Promise<boolean> {
    try {
      await unlink(this.pathFor(id));
      return true;
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return false;
      throw e;
    }
  }

  /** Test whether a memory exists by id. */
  async has(id: string): Promise<boolean> {
    try {
      await stat(this.pathFor(id));
      return true;
    } catch {
      return false;
    }
  }

  /** Absolute path of a memory file (file may not exist). */
  pathFor(id: string): string {
    return join(this.dir, `${id}.md`);
  }
}
